import {
  Component,
  computed,
  HostListener,
  inject,
  Input,
  OnInit,
  PLATFORM_ID,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { EventType, Router } from '@angular/router';
import {
  NbLayoutComponent,
  NbLayoutHeaderComponent,
  NbLayoutModule,
  NbMediaBreakpoint,
  NbMediaBreakpointsService,
  NbMenuBag,
  NbMenuItem,
  NbMenuModule,
  NbMenuService,
  NbSidebarModule,
  NbSidebarService,
  NbThemeService,
} from '@nebular/theme';
import { delay, filter, Subject, takeUntil, withLatestFrom } from 'rxjs';
import { FooterComponent, HeaderComponent } from '../../components';
import { HeaderAction } from '../header-window-action.interface';
import { KPHeaderData, State, StateService } from '@res/ngg-core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';

@Component({
  standalone: true,
  selector: 'nggt-one-column-layout',
  styleUrls: ['./one-column.layout.scss'],
  imports: [CommonModule, NbLayoutModule, NbSidebarModule, HeaderComponent, FooterComponent, NbMenuModule],
  template: `
    <nb-layout [windowMode]="isWindowMode">
      <nb-layout-header [fixed]="isHeaderFix" id="header">
        <nggt-header
          [templateRefs]="HeaderFormTemplateRefs"
          [headerData]="headerData"
          headerBrandTitle=""
        ></nggt-header>
      </nb-layout-header>

      <nb-sidebar class="menu-sidebar" tag="menu-sidebar" responsive [end]="isMenuSidebarPositionEnd()">
        <ng-content select="nb-menu"></ng-content>
      </nb-sidebar>

      @if (subMenu().length) {
        <nb-layout-column start class="subMenu">
          <nb-menu class="mat-elevation-z1" [items]="subMenu()"></nb-menu>
        </nb-layout-column>
      }

      <nb-layout-column class="main-content">
        <ng-content select="router-outlet"></ng-content>
      </nb-layout-column>

      <nb-layout-footer *ngIf="isFooterVisibleIs()" fixed>
        <nggt-footer></nggt-footer>
      </nb-layout-footer>

      <nb-sidebar
        tag="notification-sidebar"
        state="collapsed"
        class="settings-sidebar"
        fixed
        [end]="isSettingsSidebarPositionEnd()"
      >
        <!-- Both shells supply <lib-notification-list>, the real selector of
             NotificationListComponent. The old single selector matched only the car-rental
             ngx-notifications page component, so the projected list was discarded and the
             sidebar opened empty. Both are accepted so neither supplier breaks. -->
        <ng-content select="lib-notification-list, ngx-notifications"></ng-content>
      </nb-sidebar>
    </nb-layout>
  `,
})
export class OneColumnLayoutComponent implements OnInit {
  @Input({ required: false }) isWindowMode = false;
  @Input({ required: false }) HeaderFormTemplateRefs: HeaderAction[] = [];
  @Input({ required: true }) headerData!: KPHeaderData;
  @Input({ required: false }) menu: NbMenuItem[] = [];
  @ViewChild(NbLayoutComponent, { static: false }) layoutComponent!: NbLayoutComponent;
  @ViewChild(NbLayoutHeaderComponent, { static: false }) layoutHeader!: NbLayoutHeaderComponent;
  @HostListener('window:scroll', ['$event.target'])
  onWindowScroll(doc: Document) {
    if (window.innerWidth > 575) return;
    const scrollTop = window.scrollY || 0;

    if (scrollTop > this.lastScrollTop) {
      // scroll down
      if (this.firstScrollUp && this.isHeaderFix) this.isHeaderFix = false;
      this.firstScrollUp = false;
    } else {
      // scroll up      remove fixed top value
      const element = doc?.getElementById('header');
      const elementTop = element?.getBoundingClientRect().top;
      if (!this.firstScrollUp && scrollTop > 0 && elementTop && elementTop < scrollTop - element.offsetHeight)
        this.isHeaderFix = true;

      if (elementTop && elementTop > scrollTop) this.isHeaderFix = true;

      this.firstScrollUp = true;
    }
    this.lastScrollTop = scrollTop;
  }

  private lastScrollTop = 0;
  private firstScrollUp = true;
  private router = inject(Router);
  protected stateService = inject(StateService);
  protected menuService = inject(NbMenuService);
  protected themeService = inject(NbThemeService);
  protected bpService = inject(NbMediaBreakpointsService);
  protected sidebarService = inject(NbSidebarService);
  protected platformId = inject(PLATFORM_ID);

  protected destroy$ = new Subject<void>();
  public subMenu: WritableSignal<NbMenuItem[]> = signal([]);

  private mediaQueriesSig = toSignal(this.themeService.onMediaQueryChange());
  private breakpointsMap = this.bpService.getBreakpointsMap();
  public isFooterVisibleIs = computed(() => {
    const mediaQueries = this.mediaQueriesSig();
    return mediaQueries?.length === 2 ? this.breakpointsMap[mediaQueries[1].name] > this.breakpointsMap['is'] : true;
  });
  public sidebar?: State;
  public layout?: State;
  public isHeaderFix = true;

  ngOnInit(): void {
    this.stateService
      .onLayoutState()
      .pipe(takeUntil(this.destroy$))
      .subscribe((layout) => (this.layout = layout));

    this.stateService
      .onSidebarState()
      .pipe(takeUntil(this.destroy$))
      .subscribe((sidebar) => (this.sidebar = sidebar));

    const isBp = this.bpService.getByName('is');
    this.menuService
      .onItemSelect()
      .pipe(withLatestFrom(this.themeService.onMediaQueryChange()), delay(20), takeUntil(this.destroy$))
      .subscribe(([, [, bpTo]]: [NbMenuBag, NbMediaBreakpoint[]]) => {
        if (bpTo.width <= isBp.width) {
          this.sidebarService.collapse('menu-sidebar');
        }
      });
    this.setSubMenu(this.router.routerState.snapshot.url);
    this.router.events
      .pipe(filter((x) => x.type === EventType.NavigationEnd))
      .subscribe((event) => this.setSubMenu(event.url));
  }

  setSubMenu(routerState: string): void {
    const subMenu = [];
    const routerStateUrlSplit = routerState.split('/');
    const data = this.menu.find(
      (menuItem) => menuItem.title.toLowerCase() === routerStateUrlSplit[1] && !menuItem.home,
    );
    if (!data) {
      this.subMenu.set([]);
      return;
    }
    subMenu.push({ title: routerStateUrlSplit[1].toUpperCase(), group: true });
    data.children?.forEach((child) => subMenu.push(child));
    this.subMenu.set(subMenu);
  }

  isMenuSidebarPositionEnd(): boolean {
    return this.sidebar?.id === 'end';
  }

  isSettingsSidebarPositionEnd(): boolean {
    return !this.isMenuSidebarPositionEnd();
  }
}
